import { NetworkStatus } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, Link } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import {
  ADMIN_ACCOUNT_EDIT_QUERY,
  AdminAccountEditPage,
} from '@web/src/features/admin/admin-account-edit-page/admin-account-edit-page'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import type { AdminAccountEditQuery } from 'types/graphql'

export const Route = createFileRoute('/(auth)/(admin-layout)/admin/accounts/$userId/edit')({
  component: AdminAccountEditRoute,
  head: () => ({
    meta: [
      { title: `Admin — Edit account | ${env.VITE_APP_NAME}` },
      { name: 'description', content: 'Edit user account and profile' },
      { name: 'robots', content: 'nofollow' },
    ],
  }),
  loader: ({ context, params }) => {
    const queryRef = context.preloadQuery(ADMIN_ACCOUNT_EDIT_QUERY, {
      variables: { id: params.userId },
    })
    return { queryRef }
  },
})

function AdminAccountEditRoute() {
  const { t } = useTranslation()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<AdminAccountEditQuery>)

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) return <SFPageLoading />
  if (error) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-8">
        <ErrorAlert error={error} />
        <div className="mt-4 flex justify-center">
          <Link className="text-primary text-sm underline underline-offset-4" to="/admin/accounts">
            {t('Back to accounts')}
          </Link>
        </div>
      </div>
    )
  }

  const user = data?.adminUser
  if (!user) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-8">
        <ErrorAlert error={new Error(t('User not found'))} />
      </div>
    )
  }

  return <AdminAccountEditPage user={user} />
}
